import { createRouter, createWebHistory } from 'vue-router'
import authService from '@/services/auth.service'
import sessionService from '@/services/session.service'
import store from '@/store'
import whatsappRoutes from '@/whatsapp/router'

const routes = [
  { path: '/f/:slug', name: 'PublicForm', component: () => import('../pages/PublicFormPage.vue'), meta: { requiresAuth: false } },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../pages/LoginPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../pages/ResetPasswordPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('../pages/VerifyEmailPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    component: () => import('../layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('../pages/DashboardPage.vue')
      },
      {
        path: 'leads',
        name: 'Leads',
        component: () => import('../pages/LeadsPage.vue')
      },
      {
        path: 'leads/:id',
        name: 'LeadDetails',
        component: () => import('../pages/LeadDetailsPage.vue')
      },
      {
        path: 'kanban',
        name: 'Kanban',
        component: () => import('../pages/KanbanPage.vue')
      },
      {
        path: 'interacoes',
        name: 'Interacoes',
        component: () => import('../pages/InteractionsPage.vue')
      },
      {
        path: 'reunioes',
        name: 'Reunioes',
        component: () => import('../pages/ReunioesPage.vue')
      },
      {
        path: 'qualificacao',
        name: 'Qualificacao',
        component: () => import('../pages/QualificacaoPage.vue')
      },
      {
        path: 'metricas',
        name: 'Metricas',
        component: () => import('../pages/MetricasPage.vue')
      },
      {
        path: 'relatorios',
        name: 'Relatorios',
        component: () => import('../pages/RelatoriosPage.vue'),
        redirect: '/relatorios/recuperacao',
        children: [
          {
            path: 'recuperacao',
            name: 'RelatorioRecuperacao',
            component: () => import('../pages/RelatorioRecuperacaoPage.vue')
          },
          {
            path: 'leads',
            name: 'RelatorioLeads',
            component: () => import('../pages/RelatorioLeadsPage.vue')
          },
          {
            path: 'conversao',
            name: 'RelatorioConversao',
            component: () => import('../pages/RelatorioConversaoPage.vue')
          },
          {
            path: 'performance',
            name: 'RelatorioPerformance',
            component: () => import('../pages/RelatorioPerformancePage.vue')
          },
          {
            path: 'atividades',
            name: 'RelatorioAtividades',
            component: () => import('../pages/RelatorioAtividadesPage.vue')
          },
          {
            path: 'roi',
            name: 'RelatorioROI',
            component: () => import('../pages/RelatorioROIPage.vue')
          },
          {
            path: 'personalizado',
            name: 'RelatorioPersonalizado',
            component: () => import('../pages/RelatorioPersonalizadoPage.vue')
          }
        ]
      },
      {
        path: 'previsoes',
        name: 'Previsoes',
        component: () => import('../pages/PrevisoesPage.vue')
      },
      {
        path: 'ia',
        name: 'IA',
        component: () => import('../pages/IAPage.vue'),
        children: [
          {
            path: 'config',
            name: 'ConfiguracaoIA',
            component: () => import('../pages/ConfiguracaoIAPage.vue'),
            meta: { adminOnly: true }
          },
          {
            path: 'sugestoes',
            name: 'SugestoesIA',
            component: () => import('../pages/SugestoesIAPage.vue')
          },
          {
            path: 'automacao',
            name: 'AutomacaoIA',
            component: () => import('../pages/AutomacaoIAPage.vue'),
            meta: { adminOnly: true }
          }
        ]
      },
      {
        path: 'configuracoes',
        name: 'Configuracoes',
        redirect: '/workspace',
        meta: { adminOnly: true }
      },
      {
        path: 'usuarios',
        name: 'Usuarios',
        component: () => import('../pages/UsuariosPage.vue'),
        meta: { adminOnly: true }
      },
      {
        path: 'workspace',
        name: 'Workspace',
        component: () => import('../pages/WorkspacePage.vue'),
        meta: { adminOnly: true }
      },
      {
        path: 'faturas',
        name: 'Faturas',
        component: () => import('../pages/FaturasPage.vue'),
        meta: { adminOnly: true }
      },
      {
        path: 'forms',
        component: () => import('../pages/FormsWorkspacePage.vue'),
        redirect: '/forms/formularios',
        children: [
          { path: 'formularios', name: 'Formularios', component: () => import('../pages/FormsPage.vue'), props: { experienceKind: 'form' } },
          { path: 'quizzes', name: 'QuizzesIA', component: () => import('../pages/FormsPage.vue'), props: { experienceKind: 'quiz' } },
          { path: 'analytics', name: 'FormsAnalytics', component: () => import('../pages/FormsAnalyticsPage.vue') }
        ]
      },
      ...whatsappRoutes
    ]
  },
  // Rota para capturar caminhos não encontrados
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  }
})

// Navegação com proteção para rotas autenticadas
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!sessionService.isAuthenticated()) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else {
      const currentUser = store.state.auth.user || authService.getUser()
      if (to.matched.some(record => record.meta.adminOnly) && !(currentUser?.is_admin || ['owner', 'admin'].includes(currentUser?.role))) {
        next('/')
        return
      }
      
      // Pré-carregar dados para o Kanban quando navegar para esta página
      if (to.path === '/kanban' && store.state.leads.leads.length === 0) {
        store.dispatch('leads/fetchLeads')
      }
      
      next()
    }
  } else {
    next()
  }
})

export default router 
